import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  TruckIcon,
  MapPinIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const ShippingPolicy: React.FC = () => {
  // Ensure page starts at top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  const effectiveDate = 'May 15, 2026';

  return (
    <div className="min-h-screen py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 text-xs font-semibold text-primary bg-primary/10 border border-primary/20 rounded-full mb-4">
            <TruckIcon className="w-4 h-4" />
            Shipping & Logistics
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-text-main mb-4 tracking-tight leading-tight">
            Shipping Policy
          </h1>
          <p className="text-lg text-text-muted max-w-2xl mx-auto">
            Effective Date: <span className="text-text-main font-medium">{effectiveDate}</span>  ·  Applies to all NotesCrafter platforms
          </p>
        </motion.div>

        {/* Section 1: Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-glass/30 backdrop-blur-md border border-border-glow rounded-2xl p-8 mb-8 hover:border-primary/50 transition-colors shadow-xl"
        >
          <div className="flex flex-col md:flex-row items-start gap-6">
            <div className="bg-gradient-to-br from-primary/20 to-secondary/20 border border-primary/30 p-4 rounded-xl shadow-inner">
              <GlobeAltIcon className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-text-main mb-3">1. Overview</h2>
              <p className="text-text-muted leading-relaxed">
                This Shipping Policy applies to all print orders placed on <code className="px-1.5 py-0.5 text-xs text-primary bg-primary/10 border border-primary/20 rounded">print.notescrafter.com</code> or through the NotesCrafter Android app. We partner with <span className="text-text-main font-semibold">Shiprocket</span> to manage order dispatch, shipping, and last-mile delivery across India.
              </p>
              <p className="text-text-muted leading-relaxed mt-3 font-semibold text-sm">
                By placing a print order, you agree to this Shipping Policy along with our Terms of Service and Refund Policy.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Detailed Sections */}
        <div className="space-y-8">
          
          {/* Section 2: Delivery Coverage */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <MapPinIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">2. Delivery Coverage</h2>
            </div>
            <div className="pl-0 sm:pl-12 space-y-4">
              <p className="text-text-muted leading-relaxed">
                We currently deliver to addresses across India only. International shipping is not available at this time.
              </p>
              <p className="text-text-muted leading-relaxed bg-primary/5 border border-primary/15 rounded-lg p-4 text-sm">
                We ship to most pin codes serviced by Shiprocket's courier network. In rare cases where your pin code is not serviceable, we will notify you via WhatsApp or at your registered email address before your order enters production.
              </p>
            </div>
          </motion.div>

          {/* Section 3: Order Processing Time */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <ClockIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">3. Order Processing Time</h2>
            </div>
            <div className="pl-0 sm:pl-12 space-y-4 text-text-muted leading-relaxed">
              <p>
                Once your payment is confirmed, your order enters our production queue. Orders are typically processed and dispatched within <strong className="text-text-main font-semibold">2 to 4 business days</strong>, covering:
              </p>
              <ul className="space-y-2 bg-primary/5 p-4 border border-primary/10 rounded-lg text-sm">
                {[
                  'PDF verification and pre-press review',
                  'Printing and wiro-binding of your A4 notebook',
                  'Quality check before courier handoff'
                ].map((step, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                    <span>{step}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm">
                Processing times may be longer during high-volume periods such as examination seasons. You will receive a WhatsApp or email notification with tracking details once your order is dispatched.
              </p>
            </div>
          </motion.div>

          {/* Section 4: Estimated Delivery Time */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <TruckIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">4. Estimated Delivery Time</h2>
            </div>
            <div className="pl-0 sm:pl-12 space-y-4 text-text-muted leading-relaxed">
              <p>
                After dispatch, estimated delivery timelines depend on your location and the courier assigned by Shiprocket:
              </p>
              <ul className="space-y-3">
                {[
                  { region: 'Metro cities', timeline: '2–4 business days', details: '(Delhi, Mumbai, Bengaluru, Hyderabad, Chennai, Kolkata, Pune)' },
                  { region: 'Tier-2 and Tier-3 cities', timeline: '3–6 business days', details: '' },
                  { region: 'Remote or rural areas', timeline: '5–10 business days', details: '' }
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 border-l-2 border-primary/20 pl-4 py-1">
                    <div>
                      <span className="font-bold text-text-main block">{item.region}: <span className="text-primary font-semibold">{item.timeline}</span></span>
                      {item.details && <span className="text-xs text-text-muted/80">{item.details}</span>}
                    </div>
                  </li>
                ))}
              </ul>
              <p className="text-sm bg-primary/5 p-4 rounded-lg border border-primary/10 mt-4 text-text-muted/90">
                These are estimates only and are not guaranteed. Delays may occur due to courier capacity, public holidays, weather, or circumstances beyond our control. NotesCrafter is not liable for delays caused by courier or third-party logistics partners.
              </p>
            </div>
          </motion.div>

          {/* Section 5: Order Tracking */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <MagnifyingGlassIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">5. Order Tracking</h2>
            </div>
            <div className="pl-0 sm:pl-12 text-text-muted leading-relaxed space-y-3">
              <p>
                Once your order is dispatched, a shipment tracking link will be sent to you via WhatsApp or email. You can also track your order directly through the NotesCrafter website or Android app using your Order ID.
              </p>
              <p className="text-xs text-text-muted/80">
                Tracking information is fetched in real-time from Shiprocket and may take up to 24 hours to update after dispatch.
              </p>
            </div>
          </motion.div>

          {/* Section 6: Shipping Charges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <CreditCardIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">6. Shipping Charges</h2>
            </div>
            <div className="pl-0 sm:pl-12 text-text-muted leading-relaxed space-y-3">
              <p>
                Shipping charges, if applicable, are calculated based on the weight and dimensions of your order and your delivery pin code. The exact cost will be displayed at checkout before payment is confirmed.
              </p>
              <p>
                We may offer free shipping promotions from time to time, which will be clearly indicated on the platform during the applicable period.
              </p>
            </div>
          </motion.div>

          {/* Section 7: Delivery Address Accuracy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <MapPinIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">7. Delivery Address Accuracy</h2>
            </div>
            <div className="pl-0 sm:pl-12 space-y-3 text-text-muted leading-relaxed">
              <p>
                You are solely responsible for providing a complete and accurate delivery address — including house/flat number, street, landmark, city, state, and pin code — along with a reachable phone number.
              </p>
              <div className="bg-red-500/5 border border-red-500/10 rounded-xl p-4 text-sm">
                <p className="font-semibold text-red-300 mb-1.5">NotesCrafter and Shiprocket are not responsible for non-delivery or delays resulting from:</p>
                <ul className="list-disc list-inside space-y-1 text-red-200/90">
                  <li>An incorrect, incomplete, or outdated address</li>
                  <li>An unreachable or unresponsive phone number at the time of delivery</li>
                  <li>Refusal to accept the package</li>
                </ul>
              </div>
              <p className="text-sm font-semibold text-text-main">
                If a delivery fails due to address errors provided by you, re-delivery charges will be borne by the customer.
              </p>
            </div>
          </motion.div>

          {/* Section 8: Failed Delivery Attempts */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <ExclamationTriangleIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">8. Failed Delivery Attempts</h2>
            </div>
            <div className="pl-0 sm:pl-12 space-y-3 text-text-muted leading-relaxed">
              <p>
                Our courier partners typically make up to 3 delivery attempts. If all attempts are unsuccessful and the package is returned to us, we will contact you via WhatsApp or email. Re-dispatch in such cases may incur additional shipping charges payable by the customer.
              </p>
              <p className="bg-red-500/5 border border-red-500/10 p-4 rounded-lg text-red-200 text-sm">
                Orders that are undeliverable and returned to origin will not be automatically refunded, as the product has already been custom-printed. Please refer to our Refund Policy for applicable exception criteria.
              </p>
            </div>
          </motion.div>

          {/* Section 9: Damaged or Tampered Shipments */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <ExclamationTriangleIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">9. Damaged or Tampered Shipments</h2>
            </div>
            <div className="pl-0 sm:pl-12 space-y-3 text-text-muted leading-relaxed">
              <p>
                If you receive a shipment that is visibly damaged or tampered with:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm bg-primary/5 p-4 border border-primary/10 rounded-lg">
                <li>Do not discard the packaging</li>
                <li>Photograph the package and the product immediately upon receipt</li>
                <li>Contact us at <a href="mailto:support@notescrafter.com" className="text-primary hover:underline font-medium">support@notescrafter.com</a> within 48 hours of delivery</li>
                <li>Include your Order ID and photographic evidence</li>
              </ul>
              <p className="text-sm font-semibold">
                If damage is confirmed to have occurred during transit and is not attributable to your source file, we may offer a reprint at our discretion.
              </p>
            </div>
          </motion.div>

          {/* Section 10: Order Cancellation Before Dispatch */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="bg-glass/20 backdrop-blur-sm border border-border-glow rounded-xl p-8 hover:border-primary/30 transition-colors shadow-lg"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-primary/10 p-2 rounded-lg border border-primary/20">
                <ArrowPathIcon className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-main">10. Order Cancellation Before Dispatch</h2>
            </div>
            <div className="pl-0 sm:pl-12 space-y-3 text-text-muted leading-relaxed">
              <p>
                Orders may be cancelled only before they enter production. To request a cancellation, contact <a href="mailto:support@notescrafter.com" className="text-primary hover:underline font-medium">support@notescrafter.com</a> immediately after placing your order with your Order ID.
              </p>
              <p className="bg-red-500/5 border border-red-500/10 p-4 rounded-lg text-red-200 text-sm">
                Once an order has entered the print and binding stage, cancellation is no longer possible. We cannot guarantee that a cancellation request will be processed in time, as production may begin within hours of order confirmation. The no-refund policy applies to all orders in production or beyond.
              </p>
            </div>
          </motion.div>

          {/* Section 11: Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85 }}
            className="bg-glass/30 backdrop-blur-md border border-primary/20 rounded-xl p-8 hover:border-primary/40 transition-colors shadow-xl"
          >
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div>
                <h2 className="text-2xl font-bold text-text-main mb-2">11. Contact Us</h2>
                <div className="space-y-1 text-text-muted text-sm sm:text-base">
                  <p className="font-semibold text-text-main">NotesCrafter</p>
                  <p className="flex items-center gap-1.5">
                    <MapPinIcon className="w-4 h-4 text-primary flex-shrink-0" />
                    Sole Proprietorship · Aurangabad, Bihar, India
                  </p>
                </div>
              </div>
              <a
                href="mailto:support@notescrafter.com"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary-hover text-white font-semibold rounded-xl transition-all shadow-lg shadow-primary/25 hover:scale-[1.02] active:scale-[0.98]"
              >
                <EnvelopeIcon className="w-5 h-5" />
                support@notescrafter.com
              </a>
            </div>
          </motion.div>

        </div>
      </div>
    </div>
  );
};

export default ShippingPolicy;
